import { useState, useCallback } from 'react';
import imageCompression from 'browser-image-compression';
import { getPresignedUploadUrl } from '@/doi-tac-module/actions/s3';
import { AgencyProfile, S3File } from '@/doi-tac-module/lib/types';
import { useToast } from '@/doi-tac-module/hooks/use-toast';
import { agencyTranslations } from '@/doi-tac-module/constants/agency-translations';

interface UseAgencyImageUploadProps {
    uid?: string;
    language: 'vi' | 'bn';
    onUpdate: (updates: Partial<AgencyProfile>) => Promise<boolean>;
}

export type AgencyImageField = 'representativePhoto' | 'companyLogo' | 'bannerPhoto';

export const useAgencyImageUpload = ({ uid, language, onUpdate }: UseAgencyImageUploadProps) => {
    const { toast } = useToast();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<Record<string, number>>({});
    
    // Cropper State
    const [isCropperOpen, setIsCropperOpen] = useState(false);
    const [croppingImage, setCroppingImage] = useState<string | null>(null);
    const [croppingField, setCroppingField] = useState<AgencyImageField | null>(null);
    const [originalFiles, setOriginalFiles] = useState<Record<string, File>>({});

    // History State
    const [isHistoryOpen, setIsHistoryOpen] = useState(false);
    const [historyField, setHistoryField] = useState<AgencyImageField | null>(null);

    const onFileSelected = useCallback((e: React.ChangeEvent<HTMLInputElement>, field: AgencyImageField) => {
        const file = e.target.files?.[0];
        if (!file) return;

        setOriginalFiles(prev => ({ ...prev, [field]: file }));
        const reader = new FileReader();
        reader.onload = () => {
            setCroppingImage(reader.result as string);
            setCroppingField(field);
            setIsCropperOpen(true);
        };
        reader.readAsDataURL(file);
        e.target.value = '';
    }, []);

    const handleRecrop = useCallback((imageUrl: string, field: AgencyImageField) => {
        setCroppingImage(imageUrl);
        setCroppingField(field);
        setIsCropperOpen(true);
    }, []);

    const handleOpenHistory = useCallback((field: AgencyImageField) => {
        setHistoryField(field);
        setIsHistoryOpen(true);
    }, []);

    const handleCropComplete = async (file: File, currentProfile: Partial<AgencyProfile>) => {
        if (!croppingField || !uid) return;
        
        setIsSubmitting(true);
        setIsCropperOpen(false);
        
        try {
            const progressKey = croppingField;
            setUploadProgress(prev => ({ ...prev, [progressKey]: 0 }));

            // Helper to upload a single file
            const uploadToS3 = async (f: File, key: string, cType: string) => {
                const res = await getPresignedUploadUrl({ key, contentType: cType });
                if (!res.success || !res.signedUrl) throw new Error("Could not get presigned url");
                
                await new Promise((resolve, reject) => {
                    const xhr = new XMLHttpRequest();
                    xhr.open('PUT', res.signedUrl!);
                    xhr.setRequestHeader('Content-Type', cType);
                    xhr.upload.onprogress = (event) => {
                        if (event.lengthComputable) {
                            const percent = Math.round((event.loaded * 100) / event.total);
                            setUploadProgress(prev => ({ ...prev, [progressKey]: percent }));
                        }
                    };
                    xhr.onload = () => xhr.status >= 200 && xhr.status < 300 ? resolve(xhr.response) : reject(new Error(`Status ${xhr.status}`));
                    xhr.onerror = () => reject(new Error('Upload failed'));
                    xhr.send(f);
                });
                return { url: res.signedUrl.split('?')[0], key };
            };

            const options = { maxSizeMB: 1, maxWidthOrHeight: 1024, useWebWorker: true };
            const compressedCroppedFile = await imageCompression(file, options);
            
            const croppedFileKey = `temp/doi-tac/${uid}/${croppingField}/${Date.now()}-cropped.webp`;
            
            // Upload Cropped
            const croppedS3 = await uploadToS3(compressedCroppedFile, croppedFileKey, 'image/webp');
            
            // Upload Original if we have it
            let originalS3 = null;
            const origFile = originalFiles[croppingField];
            if (origFile) {
                 const origFileKey = `temp/doi-tac/${uid}/${croppingField}-original/${Date.now()}.${origFile.name.split('.').pop()}`;
                 originalS3 = await uploadToS3(origFile, origFileKey, origFile.type || 'image/jpeg');
            }

            const historyFieldName = `${croppingField}History` as keyof AgencyProfile;
            const originalFieldName = `${croppingField}Original` as keyof AgencyProfile;
            
            const currentHistory = (currentProfile[historyFieldName] as string[]) || [];
            const newHistory = [croppedS3.url, ...currentHistory.filter((url: string) => url !== croppedS3.url)].slice(0, 10);
            
            const updates: Partial<AgencyProfile> = {
                [croppingField]: croppedS3,
                [historyFieldName]: newHistory
            };
            
            if (originalS3) {
                (updates as any)[originalFieldName] = originalS3;
            }

            const success = await onUpdate(updates);
            
            if (success) {
                toast({ 
                    title: language === 'vi' ? "Cập nhật ảnh thành công" : (language === 'bn' ? "ছবি সফলভাবে আপডেট হয়েছে" : "Image updated successfully"), 
                    className: 'bg-green-500 text-white' 
                });
            }

            // Clear original file state
            setOriginalFiles(prev => {
                const next = { ...prev };
                delete next[croppingField];
                return next;
            });

        } catch (error) {
            console.error("Crop error:", error);
            const imageActions = agencyTranslations.imageActions;
            toast({ 
                variant: 'destructive', 
                title: imageActions.cropErrorTitle[language], 
                description: imageActions.cropErrorDesc[language] 
            });
        } finally {
            setIsSubmitting(false);
            setCroppingField(null);
            setCroppingImage(null);
            setTimeout(() => setUploadProgress(prev => {
                const next = { ...prev };
                delete next[croppingField || ''];
                return next;
            }), 1000);
        }
    };

    const handleHistorySelect = async (url: string) => {
        if (!historyField) return;
        
        setIsSubmitting(true);
        const success = await onUpdate({ [historyField]: url });
        
        if (success) {
            setIsHistoryOpen(false);
            setHistoryField(null);
            toast({ 
                title: language === 'vi' ? 'Đã đổi ảnh' : (language === 'bn' ? 'ছবি পরিবর্তন করা হয়েছে' : 'Image changed'), 
                className: 'bg-green-500 text-white' 
            });
        }
        setIsSubmitting(false);
    };

    const handleHistoryDelete = async (url: string, currentProfile: Partial<AgencyProfile>): Promise<void> => {
        if (!historyField) return;
        
        const historyFieldName = `${historyField}History` as keyof AgencyProfile;
        const currentHistory = (currentProfile[historyFieldName] as string[]) || [];
        
        if (currentHistory.length <= 1) {
            toast({ 
                variant: 'destructive', 
                title: language === 'vi' ? 'Không thể xóa' : (language === 'bn' ? 'মুছে ফেলা যাবে না' : 'Cannot delete'), 
                description: language === 'vi' ? 'Bạn phải giữ lại ít nhất 1 ảnh trong lịch sử' : (language === 'bn' ? 'আপনাকে ইতিহাসে অন্তত ১টি ছবি রাখতে হবে' : 'You must keep at least 1 image in history') 
            });
            return;
        }

        const updatedHistory = currentHistory.filter((item: string) => item !== url);
        const updates: Partial<AgencyProfile> = { [historyFieldName]: updatedHistory };
        
        // If current image is the one being deleted, switch to the first remaining one
        const currentImageUrl = typeof currentProfile[historyField] === 'string' 
            ? currentProfile[historyField] 
            : (currentProfile[historyField] as any)?.url;
            
        if (currentImageUrl === url) {
            updates[historyField] = updatedHistory[0];
        }

        await onUpdate(updates);
    };

    const getOriginalImageUrl = (profile: Partial<AgencyProfile>, field: AgencyImageField) => {
        const originalFieldName = `${field}Original` as keyof AgencyProfile;
        const value = profile[originalFieldName] || profile[field];
        if (!value) return null;
        if (typeof value === 'string') return value;
        if (value && typeof value === 'object' && 'url' in value) return (value as any).url;
        return null;
    };
    
    const getImageUrl = (profile: Partial<AgencyProfile>, field: AgencyImageField) => {
        const val = profile[field];
        if (!val) return null;
        return typeof val === 'string' ? val : (val as any).url;
    };

    return {
        isSubmitting,
        uploadProgress,
        isCropperOpen,
        setIsCropperOpen,
        croppingImage,
        croppingField,
        isHistoryOpen,
        setIsHistoryOpen,
        historyField,
        onFileSelected,
        handleRecrop,
        handleOpenHistory,
        handleCropComplete,
        handleHistorySelect,
        handleHistoryDelete,
        getOriginalImageUrl,
        getImageUrl
    };
};
